export const getToday = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// 신체 정보
export class ClosetBody {
  id = 0;
  userId = 0;
  gender = "";
  height: number | null = null;
  weight: number | null = null;
  shoulderWidth: number | null = null;
  chest: number | null = null;
  waist: number | null = null;
  hip: number | null = null;
  armLength: number | null = null;
  legLength: number | null = null;
  rise: number | null = null;
  totalLength: number | null = null;
  thigh: number | null = null;
  footSize: number | null = null;
  isPublic = false;
  createDatetime = "";
  updateDatetime = "";
}

// 신체정보 히스토리
export class ClosetBodyHistory {
  id = 0;
  userId = 0;
  gender = "";
  height: number | null = null;
  weight: number | null = null;
  shoulderWidth: number | null = null;
  chest: number | null = null;
  waist: number | null = null;
  hip: number | null = null;
  armLength: number | null = null;
  legLength: number | null = null;
  rise: number | null = null;
  totalLength: number | null = null;
  thigh: number | null = null;
  footSize: number | null = null;
  measuredDate = "";
  createDatetime = "";
}

// 의류 기록
export class ClosetClothing {
  id = 0;
  category = "";
  subCategory = "";
  brandName = "";
  productName = "";
  labeledSize = "";
  purchaseDate = "";
  satisfaction: number | null = null;
  fitFeedback = "";
  memo = "";
  measurements: Record<string, number> = {};
  createDatetime = "";
  updateDatetime = "";
}

// 의류 등록/수정 요청
export class ClosetClothingRequest {
  category = "TOP";
  subCategory = "";
  brandName = "";
  productName = "";
  labeledSize = "";
  purchaseDate: string | null = getToday();
  satisfaction: number | null = null;
  fitFeedback = "";
  memo = "";
  measurements: Record<string, number | null> = {};

  public reset() {
    this.category = "TOP";
    this.subCategory = "";
    this.brandName = "";
    this.productName = "";
    this.labeledSize = "";
    this.purchaseDate = getToday();
    this.satisfaction = null;
    this.fitFeedback = "";
    this.memo = "";
    this.measurements = {};
  }

  public bindFromClothing(clothing: ClosetClothing) {
    this.category = clothing.category;
    this.subCategory = clothing.subCategory;
    this.brandName = clothing.brandName;
    this.productName = clothing.productName;
    this.labeledSize = clothing.labeledSize;
    this.purchaseDate = clothing.purchaseDate;
    this.satisfaction = clothing.satisfaction;
    this.fitFeedback = clothing.fitFeedback;
    this.memo = clothing.memo;
    this.measurements = { ...clothing.measurements };
  }
}

// 카테고리 옵션
export const CLOTHING_CATEGORY_OPTIONS = [
  { value: "TOP", label: "상의" },
  { value: "BOTTOM", label: "하의" },
  { value: "OUTER", label: "겉옷" },
  { value: "ONEPIECE", label: "원피스" },
  { value: "SHOES", label: "신발" },
];

// 핏 평가 옵션
export const FIT_FEEDBACK_OPTIONS = [
  { value: "VERY_TIGHT", label: "매우 작음" },
  { value: "TIGHT", label: "작음" },
  { value: "PERFECT", label: "딱 맞음" },
  { value: "LOOSE", label: "큼" },
  { value: "VERY_LOOSE", label: "매우 큼" },
];

// 소분류 추천 (자유입력, 콤보박스 제안)
export const SUB_CATEGORY_SUGGESTIONS: Record<string, string[]> = {
  TOP: [
    "반팔 티셔츠", "긴팔 티셔츠", "맨투맨", "후드", "셔츠",
    "블라우스", "니트/스웨터", "폴로", "나시/탱크탑", "크롭탑",
  ],
  BOTTOM: [
    "청바지", "슬랙스", "반바지", "긴바지", "핫팬츠",
    "조거팬츠", "트레이닝팬츠", "치마", "롱스커트", "레깅스",
  ],
  OUTER: [
    "자켓", "블레이저", "가디건", "패딩", "롱패딩",
    "조끼패딩", "코트", "롱코트", "바람막이", "플리스",
    "레더/라이더스", "집업",
  ],
  ONEPIECE: ["원피스", "점프수트", "오버올"],
  SHOES: [
    "스니커즈", "러닝화", "구두", "로퍼", "부츠",
    "샌들", "슬리퍼",
  ],
};

// 카테고리별 측정 항목 매핑
export interface MeasurementField {
  key: string;
  label: string;
  unit: string;
}

export const MEASUREMENT_FIELDS: Record<string, MeasurementField[]> = {
  TOP: [
    { key: "total_length", label: "총장", unit: "cm" },
    { key: "shoulder_width", label: "어깨너비", unit: "cm" },
    { key: "sleeve_length", label: "소매길이", unit: "cm" },
    { key: "armhole", label: "암홀", unit: "cm" },
    { key: "chest_width", label: "가슴단면", unit: "cm" },
    { key: "hem_width", label: "밑단단면", unit: "cm" },
  ],
  BOTTOM: [
    { key: "total_length", label: "총장", unit: "cm" },
    { key: "waist_width", label: "허리단면", unit: "cm" },
    { key: "hip_width", label: "엉덩이단면", unit: "cm" },
    { key: "thigh_width", label: "허벅지단면", unit: "cm" },
    { key: "rise", label: "밑위", unit: "cm" },
    { key: "hem_width", label: "밑단단면", unit: "cm" },
  ],
  OUTER: [
    { key: "total_length", label: "총장", unit: "cm" },
    { key: "shoulder_width", label: "어깨너비", unit: "cm" },
    { key: "sleeve_length", label: "소매길이", unit: "cm" },
    { key: "armhole", label: "암홀", unit: "cm" },
    { key: "chest_width", label: "가슴단면", unit: "cm" },
    { key: "hem_width", label: "밑단단면", unit: "cm" },
  ],
  ONEPIECE: [
    { key: "total_length", label: "총장", unit: "cm" },
    { key: "shoulder_width", label: "어깨너비", unit: "cm" },
    { key: "chest_width", label: "가슴단면", unit: "cm" },
    { key: "waist_width", label: "허리단면", unit: "cm" },
    { key: "hip_width", label: "엉덩이단면", unit: "cm" },
    { key: "sleeve_length", label: "소매길이", unit: "cm" },
  ],
  SHOES: [
    { key: "foot_length", label: "발길이", unit: "mm" },
    { key: "ball_width", label: "발볼너비", unit: "mm" },
  ],
};

// 라벨 헬퍼
export const getCategoryLabel = (value: string): string => {
  return CLOTHING_CATEGORY_OPTIONS.find((o) => o.value === value)?.label ?? value;
};

export const getFitFeedbackLabel = (value: string): string => {
  return FIT_FEEDBACK_OPTIONS.find((o) => o.value === value)?.label ?? value;
};

export const getMeasureLabel = (category: string, key: string): string => {
  const fields = MEASUREMENT_FIELDS[category];
  if (!fields) return key;
  return fields.find((f) => f.key === key)?.label ?? key;
};

// ===== 단위(cm/inch) 변환 =====
export const UNIT_STORAGE_KEY = "closet_unit_pref"; // "cm" | "in"

const round1 = (n: number): number => Math.round(n * 10) / 10;
const round2 = (n: number): number => Math.round(n * 100) / 100;

// 기준 단위(cm 또는 mm) 값을 표시 단위로 변환
export const convertMeasure = (
  val: number,
  baseUnit: string,
  toInch: boolean
): number => {
  if (!toInch) return val;
  return baseUnit === "mm" ? round2(val / 25.4) : round1(val / 2.54);
};

// 표시 단위 라벨 (cm→in, mm→in, 나머지는 그대로)
export const displayUnit = (baseUnit: string, toInch: boolean): string => {
  if (!toInch) return baseUnit;
  return "in";
};

// 값 + 단위 문자열 (예: "52cm", "20.5in")
export const formatMeasure = (
  val: number | null | undefined,
  baseUnit: string,
  toInch: boolean
): string => {
  if (val == null) return "-";
  return `${convertMeasure(Number(val), baseUnit, toInch)}${displayUnit(baseUnit, toInch)}`;
};

// ===== 핏 평가 기반 '내게 맞는 사이즈' 추천 =====
const FIT_OFFSET: Record<string, number> = {
  VERY_TIGHT: -2,
  TIGHT: -1,
  PERFECT: 0,
  LOOSE: 1,
  VERY_LOOSE: 2,
};

export interface FitRecoRow {
  key: string;
  label: string;
  unit: string; // 기준 단위 cm/mm
  recommended: number | null; // 추천 수치(기준 단위)
  low: number | null; // 범위 하한
  high: number | null; // 범위 상한
  boundType: "" | "range" | "atLeast" | "atMost";
  basis: string; // 근거 설명(한글)
  sampleCount: number; // 핏 평가가 있는 표본 수
}

// 소분류(반팔/긴팔, 반바지/긴바지 등)에 따라 값이 크게 달라지는 '길이형' 부위.
// 이 부위는 소분류별로 나눠서 추천하고, 나머지(어깨·가슴·허리 등 몸 기반)는 공통 추천한다.
const LENGTH_DEPENDENT_KEYS: Record<string, string[]> = {
  TOP: ["total_length", "sleeve_length"],
  OUTER: ["total_length", "sleeve_length"],
  ONEPIECE: ["total_length", "sleeve_length"],
  BOTTOM: ["total_length"],
  SHOES: [],
};

// 단일 부위(field)에 대해 주어진 옷 목록으로 추천 수치 계산
const computeFieldReco = (field: MeasurementField, list: any[]): FitRecoRow => {
  const samples: { v: number; off: number }[] = [];
  list.forEach((c) => {
    const raw = c.measurements ? c.measurements[field.key] : null;
    const off = FIT_OFFSET[c.fitFeedback];
    if (raw != null && Number(raw) > 0 && off !== undefined) {
      samples.push({ v: Number(raw), off });
    }
  });

  const base: FitRecoRow = {
    key: field.key,
    label: field.label,
    unit: field.unit,
    recommended: null,
    low: null,
    high: null,
    boundType: "",
    basis: "핏 평가 데이터 없음",
    sampleCount: 0,
  };
  if (samples.length === 0) return base;
  base.sampleCount = samples.length;

  const perfects = samples.filter((s) => s.off === 0).map((s) => s.v);
  if (perfects.length) {
    const avg = perfects.reduce((a, b) => a + b, 0) / perfects.length;
    const min = Math.min(...perfects);
    const max = Math.max(...perfects);
    base.recommended = round1(avg);
    base.low = round1(min);
    base.high = round1(max);
    base.boundType = min === max ? "" : "range";
    base.basis = `딱맞음 ${perfects.length}벌 기준`;
    return base;
  }

  const smalls = samples.filter((s) => s.off < 0).map((s) => s.v);
  const bigs = samples.filter((s) => s.off > 0).map((s) => s.v);
  const maxSmall = smalls.length ? Math.max(...smalls) : null;
  const minBig = bigs.length ? Math.min(...bigs) : null;

  if (maxSmall != null && minBig != null) {
    base.recommended = round1((maxSmall + minBig) / 2);
    base.low = round1(Math.min(maxSmall, minBig));
    base.high = round1(Math.max(maxSmall, minBig));
    base.boundType = "range";
    base.basis = "작음·큼 사이";
    return base;
  }
  if (maxSmall != null) {
    base.recommended = round1(maxSmall);
    base.low = round1(maxSmall);
    base.boundType = "atLeast";
    base.basis = "작게 나온 옷 기준 · 이상 권장";
    return base;
  }
  base.recommended = round1(minBig as number);
  base.high = round1(minBig as number);
  base.boundType = "atMost";
  base.basis = "크게 나온 옷 기준 · 이하 권장";
  return base;
};

export interface LengthGroupItem {
  subCategory: string;
  row: FitRecoRow;
}
export interface LengthGroup {
  key: string;
  label: string;
  unit: string;
  items: LengthGroupItem[]; // 소분류별 추천 (데이터 있는 것만)
}
export interface FitRecoResult {
  common: FitRecoRow[]; // 공통 부위 (소분류 무관)
  lengthGroups: LengthGroup[]; // 소분류별로 달라지는 길이형 부위
}

// 같은 카테고리 의류들의 (부위별 실측치 + 핏평가)를 비교해 '딱 맞는' 수치를 추정.
// 공통 부위는 전체 집계, 길이형 부위는 소분류별로 나눠 계산한다.
export const computeFitReco = (
  category: string,
  clothingList: any[]
): FitRecoResult => {
  const fields = MEASUREMENT_FIELDS[category] || [];
  const lengthKeys = LENGTH_DEPENDENT_KEYS[category] || [];
  const inCat = clothingList.filter((c) => c.category === category);

  const common = fields
    .filter((f) => !lengthKeys.includes(f.key))
    .map((f) => computeFieldReco(f, inCat));

  const lengthGroups = fields
    .filter((f) => lengthKeys.includes(f.key))
    .map((f) => {
      const groups: Record<string, any[]> = {};
      inCat.forEach((c) => {
        const sub = (c.subCategory && String(c.subCategory).trim()) || "기타";
        if (!groups[sub]) groups[sub] = [];
        groups[sub].push(c);
      });
      const items: LengthGroupItem[] = Object.entries(groups)
        .map(([sub, list]) => ({ subCategory: sub, row: computeFieldReco(f, list) }))
        .filter((it) => it.row.recommended != null)
        .sort((a, b) => (b.row.recommended as number) - (a.row.recommended as number));
      return { key: f.key, label: f.label, unit: f.unit, items };
    })
    .filter((g) => g.items.length > 0);

  return { common, lengthGroups };
};

// Size Korea 국가기술표준원 기준 (20~30대)
export const KOREAN_AVG_BODY = {
  MALE: {
    label: "남성 평균 (20~30대)",
    height: 174.2,
    weight: 73.5,
    shoulderWidth: 39.5,
    chest: 95.3,
    waist: 82.1,
    hip: 95.8,
    armLength: 60.2,
    legLength: 77.5,
    rise: 26.5,
    totalLength: 104.0,
    thigh: 55.2,
    footSize: 265,
  },
  FEMALE: {
    label: "여성 평균 (20~30대)",
    height: 161.5,
    weight: 56.8,
    shoulderWidth: 35.8,
    chest: 85.2,
    waist: 70.5,
    hip: 93.1,
    armLength: 54.3,
    legLength: 72.8,
    rise: 25.0,
    totalLength: 97.8,
    thigh: 52.8,
    footSize: 235,
  },
} as const;
